import React, { useState, useEffect, useRef, useContext } from "react";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import {
  getGroupsDb,
  listGroups,
  createGroup,
  createGroupsBulk,
  extractGroupParticipants,
  exportGroupParticipants,
} from "../../services/groups.api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { SocketContext } from "../../context/Socket/SocketContext";

// D8.20.3 — Lógica (11 estados + effects + handlers + API) de GroupsWA.
export default function useGroupsWA() {
  const { whatsApps } = useContext(WhatsAppsContext);
  const socketManager = useContext(SocketContext);

  // Estados
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groupsFromWhatsApp, setGroupsFromWhatsApp] = useState([]);
  const [groupsFromDB, setGroupsFromDB] = useState([]);
  const [newGroups, setNewGroups] = useState([]);
  const [savedGroups, setSavedGroups] = useState(new Set());
  
  // ✅ Estados para paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // ✅ Estados para extracción y exportación de participantes
  const [extractingGroups, setExtractingGroups] = useState(new Set());
  const [exportingGroups, setExportingGroups] = useState(new Set());
  const extractionProgressRef = useRef({});

  // WhatsApps disponibles
  const connectedWhatsApps = whatsApps.filter(w => w.status === 'CONNECTED');

  // Función para listar grupos desde WhatsApp
  const handleListGroups = async () => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión WhatsApp");
      return;
    }

    setLoading(true);
    
    try {
      // 1. Obtener grupos desde WhatsApp
      const whatsappResponse = await listGroups(selectedWhatsapp);
      const groups = whatsappResponse.data.groups || [];
      
      // 2. Obtener grupos desde DB
      const dbResponse = await getGroupsDb(selectedWhatsapp);
      const dbGroups = dbResponse.data.groups || [];
      
      // ✅ CORRECCIÓN: Normalizar groupId a string para comparación correcta
      const dbGroupIds = dbGroups.map(g => String(g.groupId || '')).filter(id => id);
      
      setGroupsFromWhatsApp(groups);
      setGroupsFromDB(dbGroupIds);
      // ✅ Resetear paginación al cargar nuevos grupos
      setPage(0);
      
      // 3. Detectar grupos nuevos (que no están en DB) - comparar como strings
      const newGroupsFiltered = groups.filter(g => {
        const groupIdStr = String(g.groupId || '');
        return !dbGroupIds.includes(groupIdStr);
      });
      setNewGroups(newGroupsFiltered);
      
      toast.success(`${groups.length} grupos encontrados (${newGroupsFiltered.length} nuevos)`);
      
    } catch (err) {
      console.error('Error listando grupos:', err);
      toast.error(err.response?.data?.error || "Error al listar grupos");
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar un grupo individual
  const handleSaveGroup = async (group) => {
    if (!selectedWhatsapp) return;

    setSaving(true);
    
    try {
      // ✅ OPTIMIZACIÓN: Excluir 'participants' del payload
      const { participants, ...groupWithoutParticipants } = group;
      
      await createGroup(selectedWhatsapp, groupWithoutParticipants);
      
      // ✅ CORRECCIÓN: Actualizar contadores correctamente
      // Agregar a set de guardados
      const newSaved = new Set(savedGroups);
      newSaved.add(group.groupId);
      setSavedGroups(newSaved);
      
      // ✅ Actualizar groupsFromDB para que el contador "Guardados" se actualice
      setGroupsFromDB([...groupsFromDB, group.groupId]);
      
      // Remover de nuevos grupos
      setNewGroups(newGroups.filter(g => g.groupId !== group.groupId));
      
      toast.success(`Grupo "${group.name}" guardado exitosamente`);
      
    } catch (err) {
      console.error('Error guardando grupo:', err);
      toast.error(err.response?.data?.error || "Error al guardar grupo");
    } finally {
      setSaving(false);
    }
  };

  // Función para guardar todos los grupos nuevos
  const handleSaveAll = async () => {
    if (newGroups.length === 0) {
      toast.info("No hay grupos nuevos para guardar");
      return;
    }

    setSaving(true);
    
    try {
      // ✅ OPTIMIZACIÓN: Excluir 'participants' del payload para reducir tamaño
      // Los participants se gestionarán en una página dedicada para envío masivo
      const groupsWithoutParticipants = newGroups.map(({ participants, ...rest }) => rest);
      
      const response = await createGroupsBulk(selectedWhatsapp, { groups: groupsWithoutParticipants });
      
      // ✅ CORRECCIÓN: Manejar respuesta del backend con errores parciales
      const { saved, errors, groups: savedGroupsData, errorsDetail } = response.data || {};
      const savedCount = saved || savedGroupsData?.length || 0;
      const errorCount = errors || errorsDetail?.length || 0;
      
      // ✅ Solo actualizar con grupos que realmente se guardaron
      if (savedGroupsData && savedGroupsData.length > 0) {
        const savedGroupIds = savedGroupsData.map(g => g.groupId);
        
        // Actualizar set de guardados
        const newSaved = new Set([...savedGroups, ...savedGroupIds]);
        setSavedGroups(newSaved);
        
        // ✅ Actualizar groupsFromDB para que el contador "Guardados" se actualice
        setGroupsFromDB([...groupsFromDB, ...savedGroupIds]);
        
        // ✅ Solo remover de newGroups los que realmente se guardaron
        setNewGroups(newGroups.filter(g => !savedGroupIds.includes(g.groupId)));
      } else if (savedCount > 0) {
        // Si no hay datos detallados pero hay contador, asumir que todos se guardaron
        const allGroupIds = newGroups.map(g => g.groupId);
        const newSaved = new Set([...savedGroups, ...allGroupIds]);
        setSavedGroups(newSaved);
        setGroupsFromDB([...groupsFromDB, ...allGroupIds]);
        setNewGroups([]);
      }
      
      // ✅ Mostrar mensaje detallado con éxito y errores
      if (errorCount > 0) {
        toast.warning(`${savedCount} grupos guardados exitosamente, ${errorCount} con errores`);
      } else {
        toast.success(`${savedCount} grupos guardados exitosamente`);
      }
      
    } catch (err) {
      console.error('Error guardando grupos:', err);
      toast.error(err.response?.data?.error || "Error al guardar grupos");
    } finally {
      setSaving(false);
    }
  };

  const isGroupSaved = (groupId) => savedGroups.has(groupId);
  
  // ✅ Calcular grupos paginados
  const paginatedGroups = groupsFromWhatsApp.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // ✅ Handlers para paginación
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ✅ Función para extraer y guardar participantes
  const handleExtractParticipants = async (group) => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión WhatsApp");
      return;
    }

    const groupId = group.groupId;
    setExtractingGroups(prev => new Set([...prev, groupId]));

    try {
      const response = await extractGroupParticipants(
        selectedWhatsapp,
        groupId,
        { groupName: group.name }
      );

      if (response.data.success) {
        // ✅ Sin toast inicial - solo se mostrará el toast verde de "completed" vía Socket.IO
        // El progreso se manejará vía Socket.IO
      } else {
        toast.error(response.data.error || "Error al iniciar extracción");
        setExtractingGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });
      }
    } catch (err) {
      console.error('Error extrayendo participantes:', err);
      toast.error(err.response?.data?.error || "Error al extraer participantes");
      setExtractingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // ✅ Función para exportar participantes a Excel
  const handleExportParticipants = async (group) => {
    if (!selectedWhatsapp) {
      toast.error("Selecciona una conexión WhatsApp");
      return;
    }

    const groupId = group.groupId;
    setExportingGroups(prev => new Set([...prev, groupId]));

    try {
      const response = await exportGroupParticipants(selectedWhatsapp, groupId);

      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participantes-${groupId}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Excel exportado exitosamente para "${group.name}"`);
    } catch (err) {
      console.error('Error exportando participantes:', err);
      if (err.response?.status === 404) {
        toast.warning("No hay participantes guardados. Extrae primero los participantes.");
      } else if (err.response?.status === 501) {
        toast.warning("Funcionalidad de exportación pendiente de implementación");
      } else {
        toast.error(err.response?.data?.error || "Error al exportar participantes");
      }
    } finally {
      setExportingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // ✅ Listener de Socket.IO para eventos de extracción
  useEffect(() => {
    if (!socketManager) return;

    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    const handleExtractProgress = (data) => {
      const { action, groupId, groupName, total, saved, skipped, message, error } = data;

      if (action === "start") {
        extractionProgressRef.current[groupId] = { total: 0, saved: 0, skipped: 0 };
        // ✅ Sin toast para "start" - solo actualizar estado interno
      } else if (action === "progress") {
        extractionProgressRef.current[groupId] = { total, saved: 0, skipped: 0 };
        // ✅ Sin toast para "progress" - solo actualizar estado interno
      } else if (action === "completed") {
        const progress = extractionProgressRef.current[groupId] || {};
        const finalSaved = saved || progress.saved || 0;
        const finalSkipped = skipped || progress.skipped || 0;
        const finalTotal = total || progress.total || 0;

        toast.success(
          `✅ Extracción completada para "${groupName}": ${finalSaved} guardados, ${finalSkipped} omitidos de ${finalTotal} totales`
        );

        // ✅ Actualizar estado del grupo para mostrar check verde
        setGroupsFromWhatsApp(prev => 
          prev.map(g => 
            g.groupId === groupId 
              ? { ...g, hasParticipantsInDB: true }
              : g
          )
        );

        // Limpiar estado de extracción
        setExtractingGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });

        delete extractionProgressRef.current[groupId];
      } else if (action === "error") {
        toast.error(`❌ Error extrayendo participantes de "${groupName}": ${error || message || "Error desconocido"}`);
        
        // Limpiar estado de extracción
        setExtractingGroups(prev => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });

        delete extractionProgressRef.current[groupId];
      }
    };

    socket.on(`company-${companyId}-extractGroupParticipants`, handleExtractProgress);

    return () => {
      socket.off(`company-${companyId}-extractGroupParticipants`, handleExtractProgress);
    };
  }, [socketManager]);

  return {
    connectedWhatsApps,
    exportingGroups,
    extractingGroups,
    extractionProgressRef,
    groupsFromDB,
    groupsFromWhatsApp,
    handleChangePage,
    handleChangeRowsPerPage,
    handleExportParticipants,
    handleExtractParticipants,
    handleListGroups,
    handleSaveAll,
    handleSaveGroup,
    isGroupSaved,
    loading,
    newGroups,
    page,
    paginatedGroups,
    rowsPerPage,
    savedGroups,
    saving,
    selectedWhatsapp,
    setExportingGroups,
    setExtractingGroups,
    setGroupsFromDB,
    setGroupsFromWhatsApp,
    setLoading,
    setNewGroups,
    setPage,
    setRowsPerPage,
    setSavedGroups,
    setSaving,
    setSelectedWhatsapp,
    socketManager,
    whatsApps,
  };
}
